/* Label collision for map markers: decides which venues get a named pill and
   which degrade to a bare dot.
 *
 * Ported from v1 (app/static/app.js:1195), which is the one piece of that map
 * worth keeping verbatim. Moved into core/ — no DOM, no Lit, no Google Maps
 * types — because that makes it the first piece of map logic that can be tested
 * without a browser or an API key, and it is the piece most likely to be wrong
 * in a way nobody notices.
 *
 * The rule: every venue would prefer its labelled pill. Walking them in priority
 * order, a pill that would overlap one already placed gives up and shows its dot
 * instead. Dots never hide and never collide — the density they create is
 * information, not clutter. A pinned venue (the selected one) always keeps its
 * pill even when it overlaps, because the user just asked for it.
 *
 * Cost per run: O(V) projections over every venue, then O(K²) rectangle tests
 * over the K inside the viewport. With 521 venues and a screenful of maybe 40,
 * that is ~1,600 comparisons — cheap enough to run on every `idle`.
 */

/** A candidate label. `width` is the pill's estimated width in CSS pixels. */
export interface Placeable {
  lat: number;
  lng: number;
  width: number;
  /** Higher claims a contested slot first. */
  priority: number;
  /** Never degrades, whatever it overlaps. */
  pinned?: boolean;
  /**
   * Box height, defaulting to `PILL_H`.
   *
   * Exists so a caller can enter something that is NOT a pill as an obstacle.
   * The native client passes its cluster markers — 34-54dp circles — as
   * `pinned` entries and discards their verdict; without a height they would
   * claim a 26px box and still cover the names beneath them, which is what
   * they were doing (round 6, measured on the device).
   *
   * The web passes nothing here and is unaffected: every entry it makes is a
   * pill, and a pill is `PILL_H` tall.
   */
  height?: number;
}

/** The camera, in the terms Google's Map exposes. */
export interface Viewport {
  zoom: number;
  north: number;
  south: number;
  east: number;
  west: number;
}

/** Estimated pill height in px. Not measured — see `estimateWidth`. */
const PILL_H = 26;

/** How far beyond the viewport edge a label is still worth deciding, in px. */
const MARGIN = 60;

interface Rect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Longitude/latitude to world pixel coordinates at a given zoom (spherical
 * Mercator, 256px tiles). Only differences matter here, so the absolute origin
 * is irrelevant — what matters is that this is the same projection the map uses,
 * or overlap would be computed in the wrong space.
 */
function project(lat: number, lng: number, scale: number): { x: number; y: number } {
  // Clamped short of ±90°: the Mercator y goes to infinity at the poles, and a
  // venue at 89.99° would otherwise produce NaN and poison every comparison.
  const sin = Math.min(0.9999, Math.max(-0.9999, Math.sin((lat * Math.PI) / 180)));
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
}

const overlaps = (a: Rect, b: Rect): boolean =>
  a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;

/**
 * Decide pill-vs-dot for everything currently on screen.
 *
 * Returns a map from input index to the chosen mode, containing **only** the
 * venues inside the viewport. Everything else is absent rather than reported as
 * a dot, deliberately: an off-screen marker's presentation is invisible, and
 * writing it costs a DOM mutation per marker per pan. The caller leaves absent
 * markers exactly as they are.
 */
export function resolveLabels(
  items: readonly Placeable[],
  view: Viewport,
): Map<number, 'pill' | 'dot'> {
  const out = new Map<number, 'pill' | 'dot'>();
  if (!items.length) return out;

  const scale = 256 * Math.pow(2, view.zoom);
  const topLeft = project(view.north, view.west, scale);
  const bottomRight = project(view.south, view.east, scale);
  // A view straddling the antimeridian has west > east, which makes the x test
  // meaningless. Rare, and the failure mode of skipping it is only that a few
  // extra labels get considered.
  const wraps = view.west > view.east;

  const inView: { i: number; item: Placeable; x: number; y: number }[] = [];
  items.forEach((item, i) => {
    const p = project(item.lat, item.lng, scale);
    const inX = wraps || (p.x >= topLeft.x - MARGIN && p.x <= bottomRight.x + MARGIN);
    if (inX && p.y >= topLeft.y - MARGIN && p.y <= bottomRight.y + MARGIN) {
      inView.push({ i, item, x: p.x, y: p.y });
    }
  });

  // Pinned first, then by priority, then by index so the result is stable: the
  // same camera over the same data must always produce the same labels, or pills
  // flicker between two equally valid answers on every idle.
  inView.sort(
    (a, b) =>
      Number(b.item.pinned ?? false) - Number(a.item.pinned ?? false) ||
      b.item.priority - a.item.priority ||
      a.i - b.i,
  );

  const claimed: Rect[] = [];
  for (const { i, item, x, y } of inView) {
    // Markers anchor bottom-centre, so the pill occupies the box above its point.
    const half = item.width / 2;
    const tall = item.height ?? PILL_H;
    const rect: Rect = { x1: x - half, y1: y - tall, x2: x + half, y2: y };
    const fits = (item.pinned ?? false) || !claimed.some((c) => overlaps(rect, c));
    if (fits) claimed.push(rect);
    out.set(i, fits ? 'pill' : 'dot');
  }
  return out;
}

/**
 * Estimated rendered width of a pill, in px.
 *
 * An estimate rather than a measurement on purpose: measuring means laying out
 * 500 elements and reading `offsetWidth` off each, which forces a synchronous
 * layout per pan. 6.5px per character at 0.78rem is v1's figure, capped where
 * the label truncates; the rest is measured off the rendered pill.
 */
export function estimateWidth(name: string, hasCount: boolean): number {
  const ICON = 20 + 5; // the kind disc plus its gap
  const PADDING = 13; // 3px left + 10px right
  const COUNT = hasCount ? 28 : 0;
  return ICON + Math.min(name.length * 6.5, 140) + PADDING + COUNT;
}
